import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import StepIndicator from '../components/application/StepIndicator';
import Footer from '../components/layout/Footer';
import {
  Step1TradingProfile,
  Step2CurrentStruggle,
  Step3SeriousnessFit,
  Step4InvestmentQualification,
} from '../components/application/FormSteps';
import { isEnglishPrimaryCountry } from '../data/countries';
import { trackApplicationStart, trackApplicationStep, trackFormSubmission, trackQualifiedLead, trackNonQualifiedLead } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Application = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = 'Apply | Order Flow School'; }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', instagram: '',
    country: '', speaksEnglish: '', tradingExperience: '',
    tradingAssets: [], futuresExperience: '', biggestStruggle: [],
    consistencyBlocks: '', seriousness: '', whyOFS: '', readiness: '',
    investment: '', callWillingness: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [contactSaved, setContactSaved] = useState(false);
  const totalSteps = 4;

  const updateFormData = (updates) => setFormData(prev => ({ ...prev, ...updates }));

  const validateStep = (step) => {
    const e = {};
    switch (step) {
      case 1:
        if (!formData.firstName?.trim()) e.firstName = 'First name is required';
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Valid email required';
        if (!formData.phone?.trim()) e.phone = 'Phone is required';
        if (!formData.instagram?.trim()) e.instagram = 'Instagram is required';
        if (!formData.country) e.country = 'Select your country';
        if (formData.country && !isEnglishPrimaryCountry(formData.country) && !formData.speaksEnglish) e.speaksEnglish = 'Please select';
        if (!formData.tradingExperience) e.tradingExperience = 'Select experience';
        break;
      case 2:
        if (!formData.biggestStruggle || formData.biggestStruggle.length === 0) e.biggestStruggle = 'Select at least one';
        break;
      case 3:
        if (!formData.seriousness) e.seriousness = 'Required';
        if (!formData.readiness) e.readiness = 'Required';
        break;
      case 4:
        if (!formData.investment) e.investment = 'Required';
        if (!formData.callWillingness) e.callWillingness = 'Required';
        break;
      default: break;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const savePartialContact = async () => {
    if (contactSaved) return;
    try {
      await fetch(`${API_URL}/api/applications/partial`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: formData.firstName, lastName: formData.lastName, email: formData.email, phone: formData.phone, instagram: formData.instagram }),
      });
      setContactSaved(true);
    } catch (err) { /* silent */ }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    if (currentStep === 1) { savePartialContact(); trackApplicationStart(); }
    trackApplicationStep(currentStep);
    setCurrentStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };
  const handlePrev = () => { setCurrentStep(prev => prev - 1); window.scrollTo(0, 0); };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    setIsSubmitting(true); setSubmitError('');
    try {
      trackFormSubmission();
      const resp = await fetch(`${API_URL}/api/applications`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const result = await resp.json();
      if (resp.status === 429 || resp.status === 409) {
        setSubmitError(result.detail || 'Unable to submit application.');
        setIsSubmitting(false);
        return;
      }
      if (!resp.ok) {
        setSubmitError('Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }
      if (result.qualified) { trackQualifiedLead(); navigate('/success', { state: { firstName: formData.firstName, email: formData.email } }); }
      else { trackNonQualifiedLead(); navigate('/not-ready-yet'); }
    } catch (err) { setSubmitError('Something went wrong. Please try again.'); }
    setIsSubmitting(false);
  };

  const stepProps = { formData, updateFormData, errors };
  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1TradingProfile {...stepProps} />;
      case 2: return <Step2CurrentStruggle {...stepProps} />;
      case 3: return <Step3SeriousnessFit {...stepProps} />;
      case 4: return <Step4InvestmentQualification {...stepProps} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <nav className="g-nav" style={{ background: 'rgba(0,0,0,0.85)' }}>
        <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} className="flex flex-col leading-none" style={{ textDecoration:'none' }}>
          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:26, color:'#C9A235' }}>OFS</span>
          <span style={{ fontFamily:"'Inter',sans-serif", fontWeight:700, fontSize:7, letterSpacing:'0.25em', color:'rgba(201,162,53,0.6)', textTransform:'uppercase', marginTop:-1 }}>ORDER FLOW SCHOOL</span>
        </a>
      </nav>
      <main style={{ paddingTop: 100, paddingBottom: 40 }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <button onClick={() => navigate('/')} className="inline-flex items-center mb-4 transition-colors" style={{ fontFamily:"'Inter',sans-serif", color:'var(--text-muted)' }} onMouseEnter={e=>e.currentTarget.style.color='var(--gold)'} onMouseLeave={e=>e.currentTarget.style.color='var(--text-muted)'}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to home
            </button>
            <h1 style={{ fontFamily:"'League Spartan',sans-serif", fontSize:'clamp(32px,5vw,42px)', color:'#FFFFFF', letterSpacing:'-0.01em' }}>APPLY TO ORDER FLOW SCHOOL</h1>
            <p style={{ fontFamily:"'Inter',sans-serif", color:'var(--text-sub)', marginTop:8 }}>Complete all steps to submit your application</p>
          </div>
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
          <div className="g-card p-6 md:p-8" data-testid="application-form-container">
            {renderStep()}
            {submitError && <p className="mt-4 text-center text-sm" style={{ color:'var(--red)' }} data-testid="submit-error-message">{submitError}</p>}
            <div className="flex items-center justify-between mt-8 pt-8" style={{ borderTop:'1px solid rgba(201,162,53,0.12)', gap:16, width:'100%', padding:'32px 4px 0' }}>
              {currentStep > 1 ? (
                <button type="button" onClick={handlePrev} data-testid="prev-step-button" className="g-btn-ghost" style={{ gap:6, flexShrink:0, minWidth:100, padding:'12px 0', whiteSpace:'nowrap' }}>
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
              ) : <div />}
              {currentStep < totalSteps ? (
                <button type="button" onClick={handleNext} data-testid="next-step-button" className="g-btn" style={{ padding:'12px 32px', flex:1, maxWidth:'none', minHeight:52 }}>
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={isSubmitting} data-testid="submit-application-button" className="g-btn disabled:opacity-50 disabled:cursor-not-allowed" style={{ padding:'14px 36px', flex:1, maxWidth:'none', minHeight:52 }}>
                  {isSubmitting ? 'Submitting...' : 'Submit My Application \u2192'}
                </button>
              )}
            </div>
            {currentStep === totalSteps && <p className="text-center text-sm mt-4" style={{ fontFamily:"'Inter',sans-serif", color:'var(--text-muted)' }}>Applications are reviewed within 24-48 hours. You'll hear from us either way.</p>}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Application;
